/**
 * `SensorOverlayToolCard` — WP-8 right-rail Sensor / live overlay
 * ToolCard for the GeoViewer right-rail `ToolCardDock`.
 *
 * Activation contract (WP-2):
 *   - Active when the chrome-side `SceneSelection.toolCardKind` is
 *     `'sensor'` (layer kind `sensor` / `timeseries` /
 *     `sensor-stream-frame-v1`), OR
 *   - When a scene-index node with `kind: 'integration'` is selected
 *     (mapped to `'sensor'` by `sceneExplorer.mapIndexNodeKind`).
 *
 * Scope (per masterplan §7.8):
 *   - Play / pause / step controls (round-trip through `useViewerStore`).
 *   - Time range selector (renders {@link TimelineScrubberView}).
 *   - Mode chip — live | replay | simulated, both colour and icon
 *     distinct (`bolt` / `replay` / `science`) per
 *     `DESIGN_POLICY.md` §5 non-color cue rule.
 *   - Freshness — last-update timestamp in MONO with
 *     relative + absolute label; > 30s flips a warning Banner with
 *     `sync_problem`.
 *   - Permission-denied / offline → `<Banner>` primitives with
 *     `lock` / `cloud_off` icons.
 *   - Linked asset / layer chip — clicking dispatches
 *     `viewer:tool-card-request` to the linked target.
 *   - Alert badge — count of active alerts, red badge variant.
 *   - Source connector info (read-only): name, host (MONO),
 *     credential mask (last-4 chars).
 *
 * Design rules (BLOCKING, see `DESIGN_POLICY.md`):
 *   - Tokens only. No raw `#hex`, `rgba()`, `Npx` literals — verified
 *     by `eslint-plugin-geosoft/no-raw-visual-constants`.
 *   - All icons from the approved Material Symbols Rounded registry
 *     in `<Icon>`, per `ICON_SYSTEM_PLAYBOOK.md`.
 *   - 40 px touch targets on every interactive control via
 *     `<IconButton>`.
 *   - One primary CTA per action row.
 *   - Loading / empty / error / stale / offline / permission states
 *     all present (per `DESIGN_INVENTORY.md` "Implementation Definition
 *     Of Done").
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Chip,
  Icon,
  IconButton,
  Stack,
  Text,
  tokens,
  type IconName,
} from '@/design';
import {
  TimelineScrubberView,
  type TimelineScrubberViewProps,
} from '@/components/timeline/TimelineScrubber';
import {
  useViewerStore,
  type SensorPlayMode,
  type SensorPlayStatus,
} from '@/state/useViewerStore';
import {
  TOOL_CARD_REQUEST_EVENT,
  type ToolCardRequestDetail,
} from '@/app/sceneSelection';
import { getToolCardKindFor } from '@/app/sceneExplorer';
import {
  computeFreshnessMs,
  formatFreshness,
  highestAlertSeverity,
  isStale,
  maskCredentialLast4,
  maskEndpoint,
  SENSOR_STALE_THRESHOLD_MS,
  type SensorOverlayState,
} from './sensorModel';

// ---------------------------------------------------------------------------
// Mode chip — colour + icon (non-color cue) per DESIGN_POLICY.md §5
// ---------------------------------------------------------------------------

interface ModeMeta {
  readonly icon: IconName;
  readonly label: string;
  /** Variant chosen so each mode is colour-distinct in addition to
   *  the icon being shape-distinct. */
  readonly variant: 'success' | 'tertiary' | 'info';
}

const MODE_META: Record<SensorPlayMode, ModeMeta> = {
  // `bolt` — live energy / real-time stream (icon-system playbook).
  live: { icon: 'bolt', label: 'Live', variant: 'success' },
  // `replay` — replay of historical data (icon-system playbook).
  replay: { icon: 'replay', label: 'Replay', variant: 'tertiary' },
  // `science` — simulated / scenario data (Product Lifecycle §Simulation).
  simulated: { icon: 'science', label: 'Simulated', variant: 'info' },
};

const MODE_ORDER: readonly SensorPlayMode[] = ['live', 'replay', 'simulated'];

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

export interface SensorOverlayToolCardProps {
  readonly state: SensorOverlayState;
  readonly timeline?: Pick<
    TimelineScrubberViewProps,
    'minMs' | 'maxMs' | 'time' | 'onChange' | 'onLive'
  >;
  /**
   * Override the mode setter. Defaults to
   * `useViewerStore.setSensorPlayMode` — supplied in tests so the
   * component can be exercised without mounting the store.
   */
  readonly onModeChange?: (mode: SensorPlayMode) => void;
  /**
   * Override the play / pause toggle. Defaults to
   * `useViewerStore.toggleSensorPlayback`.
   */
  readonly onTogglePlayback?: () => void;
  /** Optional step-back action; disabled when undefined. */
  readonly onStepBack?: () => void;
  /** Optional step-forward action; disabled when undefined. */
  readonly onStepForward?: () => void;
  /** Optional override for `Date.now()` used by freshness math. */
  readonly nowMs?: number;
  readonly 'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dispatchPivot(detail: ToolCardRequestDetail): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent<ToolCardRequestDetail>(TOOL_CARD_REQUEST_EVENT, {
        detail,
      }),
    );
  } catch {
    // No-op: the event bus is best-effort, the audit log captures
    // the click separately.
  }
}

/** Stable hook — re-renders the freshness label every second so the
 *  "12 s ago" line stays accurate without manual refresh. */
function useTickingNow(intervalMs = 1000, override?: number): number {
  const [now, setNow] = useState<number>(() => override ?? Date.now());
  useEffect(() => {
    if (override !== undefined) {
      setNow(override);
      return undefined;
    }
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, override]);
  return now;
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

interface ConnectorRowProps {
  readonly connector: SensorOverlayState['connector'];
}

function ConnectorRow({ connector }: ConnectorRowProps) {
  if (!connector) return null;
  const host = maskEndpoint(connector.endpointHost);
  return (
    <Stack
      direction="column"
      gap={1}
      data-testid="sensor-connector"
      role="group"
      aria-label="Source connector"
    >
      <Stack direction="row" align="center" gap={1}>
        <Icon name="extension" size={20} decorative />
        <Text variant="body">{connector.name}</Text>
        {connector.providerType ? (
          <Chip
            label={connector.providerType}
            variant="neutral"
            aria-label={`Provider type ${connector.providerType}`}
          />
        ) : null}
      </Stack>
      {host ? (
        <Stack direction="row" align="center" gap={1}>
          <Icon name="dns" size={16} decorative />
          <span
            data-testid="sensor-connector-host"
            style={{
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
              color: tokens.color.ink.secondary,
            }}
          >
            {host}
          </span>
        </Stack>
      ) : null}
      {connector.credentialLast4 || connector.scope || connector.rateLimit ? (
        <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
          {connector.credentialLast4 ? (
            <Stack direction="row" align="center" gap={1}>
              <Icon name="key" size={16} decorative />
              <span
                data-testid="sensor-connector-credential"
                style={{
                  fontFamily: tokens.font.family.mono,
                  fontSize: tokens.font.role.engMono.size,
                  color: tokens.color.ink.muted,
                }}
              >
                {maskCredentialLast4(connector.credentialLast4)}
              </span>
            </Stack>
          ) : null}
          {connector.scope ? (
            <Chip
              icon="lock"
              label={connector.scope}
              variant="neutral"
              aria-label={`Scope ${connector.scope}`}
            />
          ) : null}
          {connector.rateLimit ? (
            <Chip
              icon="speed"
              label={connector.rateLimit}
              variant="neutral"
              aria-label={`Rate limit ${connector.rateLimit}`}
            />
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

interface LinkedTargetsRowProps {
  readonly targets: SensorOverlayState['linkedTargets'];
}

function LinkedTargetsRow({ targets }: LinkedTargetsRowProps) {
  if (!targets || targets.length === 0) return null;
  return (
    <Stack
      direction="row"
      align="center"
      gap={1}
      style={{ flexWrap: 'wrap' }}
      role="group"
      aria-label="Linked assets"
      data-testid="sensor-linked-targets"
    >
      {targets.map((target, idx) => {
        const key = `${target.label}-${target.layerId ?? target.indexNodeId ?? idx}`;
        const ariaLabel = `Open ${target.label} in tool card`;
        return (
          <Chip
            key={key}
            icon="link"
            label={target.label}
            variant="tertiary"
            pressable
            aria-label={ariaLabel}
            onClick={() => {
              dispatchPivot({
                toolCardKind: getToolCardKindFor({
                  layerKind: target.layerKind,
                  indexNodeKind: undefined,
                }),
                layerId: target.layerId,
                layerKind: target.layerKind,
                indexNodeId: target.indexNodeId,
                featureId: target.featureId,
                source: 'tool-card',
              });
            }}
          />
        );
      })}
    </Stack>
  );
}

interface ModeRowProps {
  readonly mode: SensorPlayMode;
  readonly onChange: (mode: SensorPlayMode) => void;
}

function ModeRow({ mode, onChange }: ModeRowProps) {
  return (
    <Stack
      direction="row"
      align="center"
      gap={1}
      role="radiogroup"
      aria-label="Sensor data mode"
      data-testid="sensor-mode-row"
    >
      {MODE_ORDER.map((candidate) => {
        const meta = MODE_META[candidate];
        const selected = candidate === mode;
        return (
          <Chip
            key={candidate}
            icon={meta.icon}
            label={meta.label}
            // Selected mode keeps its semantic colour; unselected modes
            // fall back to neutral so the active mode is visually
            // dominant — colour AND icon both convey the active state.
            variant={selected ? meta.variant : 'neutral'}
            pressable
            pressed={selected}
            aria-label={`${meta.label} mode${selected ? ' (active)' : ''}`}
            onClick={() => onChange(candidate)}
          />
        );
      })}
    </Stack>
  );
}

interface PlaybackRowProps {
  readonly status: SensorPlayStatus;
  readonly onToggle: () => void;
  readonly onStepBack?: () => void;
  readonly onStepForward?: () => void;
}

function PlaybackRow({
  status,
  onToggle,
  onStepBack,
  onStepForward,
}: PlaybackRowProps) {
  const isPlaying = status === 'playing';
  // One primary CTA per row (DESIGN_POLICY.md §5): the play / pause
  // button is the primary; the step buttons are secondary IconButtons
  // (40 px touch target each).
  return (
    <Stack
      direction="row"
      align="center"
      gap={1}
      role="group"
      aria-label="Playback controls"
      data-testid="sensor-playback-row"
    >
      <IconButton
        aria-label="Step back"
        onClick={onStepBack}
        disabled={!onStepBack}
        data-testid="sensor-step-back"
      >
        <Icon name="skip_previous" size={20} decorative />
      </IconButton>
      <Button
        variant="primary"
        onClick={onToggle}
        aria-label={isPlaying ? 'Pause sensor playback' : 'Play sensor playback'}
        aria-pressed={isPlaying}
        data-testid="sensor-play-toggle"
      >
        <Stack direction="row" align="center" gap={1}>
          <Icon name={isPlaying ? 'pause' : 'play_arrow'} size={20} decorative />
          <span>{isPlaying ? 'Pause' : 'Play'}</span>
        </Stack>
      </Button>
      <IconButton
        aria-label="Step forward"
        onClick={onStepForward}
        disabled={!onStepForward}
        data-testid="sensor-step-forward"
      >
        <Icon name="skip_next" size={20} decorative />
      </IconButton>
    </Stack>
  );
}

interface FreshnessRowProps {
  readonly lastUpdateIso: string | undefined;
  readonly nowMs: number;
  readonly mode: SensorPlayMode;
}

function FreshnessRow({ lastUpdateIso, nowMs, mode }: FreshnessRowProps) {
  const relative = formatFreshness(lastUpdateIso, nowMs);
  const absolute = lastUpdateIso ?? '—';
  const ms = computeFreshnessMs(lastUpdateIso, nowMs);
  // Live-mode + missing freshness is itself a degraded state but is
  // handled by upstream banners (offline / permission); the row shows
  // "never" so the user still has a label.
  const ageLabel = ms === null ? 'no data' : relative;
  return (
    <Stack
      direction="row"
      align="center"
      gap={1}
      data-testid="sensor-freshness"
      role="group"
      aria-label="Sensor freshness"
    >
      <Icon name="schedule" size={16} decorative />
      <span
        style={{
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.role.engMono.size,
          color: tokens.color.ink.secondary,
        }}
      >
        {ageLabel} — {absolute}
      </span>
      {mode === 'live' ? null : (
        <Chip
          icon={MODE_META[mode].icon}
          label={MODE_META[mode].label}
          variant="neutral"
          aria-label={`Mode ${MODE_META[mode].label}`}
        />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SensorOverlayToolCard({
  state,
  timeline,
  onModeChange,
  onTogglePlayback,
  onStepBack,
  onStepForward,
  nowMs,
  'data-testid': testId = 'sensor-overlay-tool-card',
}: SensorOverlayToolCardProps) {
  const storeMode = useViewerStore((s) => s.sensorPlayMode);
  const storeStatus = useViewerStore((s) => s.sensorPlayStatus);
  const setSensorPlayMode = useViewerStore((s) => s.setSensorPlayMode);
  const toggleSensorPlayback = useViewerStore((s) => s.toggleSensorPlayback);

  // Caller may override the controlled mode/status by passing a
  // fully-formed `state` and an `onModeChange` / `onTogglePlayback`
  // pair. The default flow round-trips through the store so the
  // playback round-trip acceptance test passes.
  const mode = state.mode ?? storeMode;
  const status = state.status ?? storeStatus;

  const handleModeChange = (next: SensorPlayMode) => {
    setSensorPlayMode(next);
    onModeChange?.(next);
  };
  const handleToggle = () => {
    toggleSensorPlayback();
    onTogglePlayback?.();
  };

  const tickingNow = useTickingNow(1000, nowMs);
  const stale = useMemo(
    () => isStale(state.lastUpdateIso, tickingNow, SENSOR_STALE_THRESHOLD_MS),
    [state.lastUpdateIso, tickingNow],
  );

  const alerts = state.alerts ?? [];
  const alertCount = alerts.length;
  const topSeverity = highestAlertSeverity(alerts);

  const isOffline = state.connectivity === 'offline';
  const isPermissionDenied = state.permission === 'permission-denied';

  const headerNode = (
    <Stack direction="row" align="center" gap={2} justify="space-between">
      <Stack direction="row" align="center" gap={1}>
        <Icon name="sensors" size={24} decorative />
        <Text variant="subtitle">{state.title ?? 'Live overlay'}</Text>
        {state.sourceId ? (
          <span
            style={{
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
              color: tokens.color.ink.muted,
            }}
          >
            {state.sourceId}
          </span>
        ) : null}
      </Stack>
      {alertCount > 0 ? (
        <Badge
          count={alertCount}
          variant="error"
          aria-label={`${alertCount} active alert${alertCount === 1 ? '' : 's'}`}
          data-testid="sensor-alert-badge"
        />
      ) : null}
    </Stack>
  );

  // ---------------- Loading ----------------
  if (state.loading) {
    return (
      <Card
        data-testid={`${testId}-loading`}
        header={headerNode}
        level={1}
      >
        <Stack direction="column" gap={2}>
          <Stack direction="row" align="center" gap={1}>
            <Icon name="pending" size={20} decorative />
            <Text variant="body">Loading live overlay…</Text>
          </Stack>
        </Stack>
      </Card>
    );
  }

  // ---------------- Error ----------------
  if (state.errorMessage) {
    return (
      <Card data-testid={`${testId}-error`} header={headerNode} level={1}>
        <Banner
          severity="error"
          title="Live overlay failed to load"
          body={state.errorMessage}
          narrationKey={`sensor-error:${state.sourceId ?? state.title ?? 'overlay'}`}
        />
      </Card>
    );
  }

  return (
    <Card data-testid={testId} header={headerNode} level={1}>
      <Stack direction="column" gap={3}>
        {/* Critical / blocking banners surface first per
            PRODUCT_LIFECYCLE_SURFACE_EXPANSION.md §"Warning, Alert,
            And Incident System". */}
        {isPermissionDenied ? (
          <Banner
            severity="info"
            // ICON_SYSTEM_PLAYBOOK.md "Status, Severity, And
            // Lifecycle Icons": Locked → `lock`.
            icon="lock"
            title="Permission denied"
            body="You do not have access to this live overlay. Ask an administrator to grant the integration scope."
            narrationKey={`sensor-permission:${state.sourceId ?? 'overlay'}`}
            data-testid="sensor-permission-banner"
          />
        ) : null}
        {isOffline ? (
          <Banner
            severity="warning"
            // ICON_SYSTEM_PLAYBOOK.md: Offline → `cloud_off`.
            icon="cloud_off"
            title="Connector offline"
            body="The source connector is unreachable. Cached values are shown until connectivity returns."
            narrationKey={`sensor-offline:${state.sourceId ?? 'overlay'}`}
            data-testid="sensor-offline-banner"
          />
        ) : null}
        {!isPermissionDenied && !isOffline && stale ? (
          <Banner
            severity="warning"
            // ICON_SYSTEM_PLAYBOOK.md: Stale → `sync_problem`.
            icon="sync_problem"
            title="Live overlay is stale"
            body={
              <span
                style={{
                  fontFamily: tokens.font.family.mono,
                  fontSize: tokens.font.role.engMono.size,
                }}
              >
                Last update {formatFreshness(state.lastUpdateIso, tickingNow)}
                {' — '}
                {state.lastUpdateIso ?? '—'}
              </span>
            }
            narrationKey={`sensor-stale:${state.sourceId ?? 'overlay'}`}
            data-testid="sensor-stale-banner"
          />
        ) : null}

        {/* Top-level alert summary — the badge in the header carries
            the count; this row repeats top-severity inline so screen
            readers announce it inside the card body. */}
        {alertCount > 0 ? (
          <Stack
            direction="row"
            align="center"
            gap={1}
            data-testid="sensor-alert-summary"
            role="status"
          >
            <Icon name="notifications_active" size={20} decorative />
            <Text variant="body">
              {alertCount} active alert{alertCount === 1 ? '' : 's'}
              {topSeverity ? ` — top severity: ${topSeverity}` : ''}
            </Text>
          </Stack>
        ) : null}

        {/* Mode chips: live / replay / simulated — chip + icon both
            update (DESIGN_POLICY.md §5). */}
        <ModeRow mode={mode} onChange={handleModeChange} />

        {/* Playback controls + freshness pair, side by side on wide
            cards but stacking on the 320 px right-rail width. */}
        <Stack direction="column" gap={2}>
          <PlaybackRow
            status={status}
            onToggle={handleToggle}
            onStepBack={onStepBack}
            onStepForward={onStepForward}
          />
          <FreshnessRow
            lastUpdateIso={state.lastUpdateIso}
            nowMs={tickingNow}
            mode={mode}
          />
        </Stack>

        {/* Time-range scrubber wired to whatever window the parent
            owns — defaults to no scrubber when the parent does not
            supply one (avoids accidental empty range). */}
        {timeline ? (
          <TimelineScrubberView
            minMs={timeline.minMs}
            maxMs={timeline.maxMs}
            time={timeline.time}
            onChange={timeline.onChange}
            onLive={timeline.onLive}
            title="Sensor window"
            liveLabel={mode === 'live' ? 'Live' : 'Now'}
            data-testid="sensor-timeline-scrubber"
          />
        ) : null}

        {/* Linked asset / layer chips — clicking dispatches a
            `viewer:tool-card-request` event so the right-rail dock
            opens the linked family card. */}
        <LinkedTargetsRow targets={state.linkedTargets} />

        {/* Connector info — read-only. */}
        <ConnectorRow connector={state.connector} />
      </Stack>
    </Card>
  );
}
